import {
  CALCULATOR_VERSION,
  FUNNEL_SCHEMA,
  isIntakeRequest,
  isContact,
  isReceipt,
  UUID,
  type IntakeRequest,
  type Contact,
  type CampaignTouch,
  type Receipt,
} from "../../../supabase/functions/_shared/marketing-contract";
import {
  browserSession,
  CAMPAIGN_ENABLED,
  CONTEXT_TTL,
  type SessionStore,
} from "./campaign-context";
import { telemetry } from "@/utils/monitoring/telemetry";
import { identifyUser } from "@/utils/monitoring/analytics";

type EventType = "assessment_completed" | "consultation_requested";
type NonContact = Exclude<IntakeRequest, { action: "contact" }>;
type Run = {
  capability: string;
  expires: number;
  start: Extract<IntakeRequest, { action: "start" }>;
  pending: NonContact[];
  milestones: Partial<Record<EventType, string>>;
  started: boolean;
  attributionStatus?: string;
  contact?: Extract<IntakeRequest, { action: "contact" }>;
  contactKey?: string;
  receipt?: Receipt;
  retryAt?: number;
  busy?: Promise<void>;
};
export type ContactState = "idle" | "saving" | "saved" | "not_saved";
export const OUTBOX_KEY = "adiastra_funnel_outbox_v1";
class IntakeError extends Error {
  constructor(
    readonly status: number,
    readonly retryAfter = 0,
  ) {
    super("intake_failed");
  }
}
interface Dependencies {
  enabled: boolean;
  endpoint: string;
  storage?: SessionStore;
  fetch: typeof fetch;
  now?: () => number;
  wait?: (ms: number) => Promise<void>;
  track: (event: string, properties: Record<string, unknown>) => void;
  identify: (contact: Contact) => void;
}
export class FunnelService {
  private runs = new Map<string, Run>();
  private states = new Map<string, ContactState>();
  private listeners = new Set<() => void>();
  private now: () => number;
  private wait: (ms: number) => Promise<void>;
  constructor(private deps: Dependencies) {
    this.now = deps.now ?? Date.now;
    this.wait =
      deps.wait ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
    if (!deps.enabled) return;
    try {
      const raw = deps.storage?.getItem(OUTBOX_KEY);
      const rows = raw && raw.length <= 40000 ? JSON.parse(raw) : [];
      if (Array.isArray(rows))
        for (const v of rows.slice(-10)) {
          if (
            !v ||
            typeof v.capability !== "string" ||
            !/^[a-f0-9]{64}$/.test(v.capability) ||
            !isIntakeRequest(v.start) ||
            v.start.action !== "start" ||
            typeof v.expires !== "number" ||
            v.expires <= this.now() ||
            v.expires > this.now() + CONTEXT_TTL ||
            !Array.isArray(v.pending)
          )
            continue;
          const pending: NonContact[] = v.pending
            .filter(
              (p: unknown): p is NonContact =>
                isIntakeRequest(p) &&
                p.action !== "contact" &&
                p.run_id === v.start.run_id,
            )
            .slice(0, 3);
          const milestones: Run["milestones"] = {};
          for (const type of [
            "assessment_completed",
            "consultation_requested",
          ] as const)
            if (
              typeof v.milestones?.[type] === "string" &&
              UUID.test(v.milestones[type])
            )
              milestones[type] = v.milestones[type];
          this.runs.set(v.start.run_id, {
            start: v.start,
            capability: v.capability,
            expires: v.expires,
            pending,
            milestones,
            started: v.started === true,
            attributionStatus: [
              "registered",
              "direct_or_unknown",
              "unregistered_or_inactive",
            ].includes(v.attributionStatus)
              ? v.attributionStatus
              : undefined,
          });
        }
    } catch {
      /* Optional bounded storage. */
    }
  }
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };
  state = (id: string): ContactState => this.states.get(id) ?? "idle";
  private setState(id: string, state: ContactState) {
    this.states.set(id, state);
    this.listeners.forEach((fn) => fn());
  }
  private persist() {
    const rows = [...this.runs.values()]
      .filter((r) => r.expires > this.now())
      .slice(-10)
      .map((r) => ({
        capability: r.capability,
        expires: r.expires,
        start: r.start,
        pending: r.pending,
        milestones: r.milestones,
        started: r.started,
        attributionStatus: r.attributionStatus,
      }));
    try {
      this.deps.storage?.setItem(OUTBOX_KEY, JSON.stringify(rows));
    } catch {
      /* Keep working in memory. */
    }
  }
  private base(id: string) {
    return {
      schema_version: 1 as const,
      run_id: id,
      event_id: crypto.randomUUID(),
      occurred_at: new Date(this.now()).toISOString(),
      calculator_version: CALCULATOR_VERSION as typeof CALCULATOR_VERSION,
    };
  }
  private emit(
    type: string,
    request: IntakeRequest,
    run: Run,
    persistence: string,
  ) {
    const ctx = run.start.context;
    try {
      this.deps.track(type, {
        schema_version: FUNNEL_SCHEMA,
        run_id: request.run_id,
        event_id: request.event_id,
        calculator_version: CALCULATOR_VERSION as typeof CALCULATOR_VERSION,
        campaign_id: ctx?.campaign_id ?? null,
        campaign_revision: ctx?.campaign_revision ?? null,
        creative_id: ctx?.creative_id ?? null,
        creative_revision: ctx?.creative_revision ?? null,
        persistence,
        attribution_status: run.attributionStatus ?? "unverified",
      });
    } catch {
      /* Analytics never changes a receipt. */
    }
  }
  begin(id: string, context: CampaignTouch | null) {
    if (!this.deps.enabled || this.runs.has(id)) return;
    const start: Run["start"] = {
      ...this.base(id),
      action: "start",
      context: context ? { ...context } : null,
    };
    const capability = Array.from(
      crypto.getRandomValues(new Uint8Array(32)),
      (b) => b.toString(16).padStart(2, "0"),
    ).join("");
    const run: Run = {
      start,
      capability,
      expires: this.now() + CONTEXT_TTL,
      pending: [],
      milestones: {},
      started: false,
    };
    this.runs.set(id, run);
    this.persist();
    this.emit("assessment_started", start, run, "pending");
    void this.flush(run);
  }
  event(id: string, type: EventType) {
    const run = this.runs.get(id);
    if (!this.deps.enabled || !run || run.milestones[type]) return;
    const request: NonContact = { ...this.base(id), action: "event", type };
    run.milestones[type] = request.event_id;
    run.pending.push(request);
    this.persist();
    this.emit(type, request, run, "pending");
    void this.flush(run);
  }
  saveContact(id: string, contact: Contact): Promise<void> {
    const run = this.runs.get(id);
    if (!this.deps.enabled) return Promise.resolve();
    if (!run || !isContact(contact)) {
      this.setState(id, "not_saved");
      return Promise.resolve();
    }
    const contactKey = JSON.stringify(contact);
    if (run.contactKey && run.contactKey !== contactKey) {
      this.setState(id, "not_saved");
      return Promise.resolve();
    }
    run.contactKey = contactKey;
    if (run.receipt) {
      this.setState(id, "saved");
      return Promise.resolve();
    }
    // Once sent, corrections cannot silently change the identity of an ambiguous retry.
    run.contact ??= {
      ...this.base(id),
      action: "contact",
      contact: { ...contact },
    };
    this.setState(id, "saving");
    return this.flush(run);
  }
  async retry(id: string) {
    const run = this.runs.get(id);
    if (!run?.contact || run.receipt) return;
    const contact = run.contact;
    this.setState(id, "saving");
    // The failed contact can be visible while a non-contact milestone is still
    // finishing. Wait for that flush before starting the requested retry.
    await run.busy;
    if (run.contact !== contact || run.receipt) return;
    return this.flush(run);
  }
  resume() {
    for (const run of this.runs.values())
      if (!run.started || run.pending.length) void this.flush(run);
  }
  forgetContact(id: string) {
    const run = this.runs.get(id);
    if (run) {
      delete run.contact;
      delete run.contactKey;
    }
    this.states.delete(id);
  }
  private async send(run: Run, request: IntakeRequest): Promise<Receipt> {
    if (!this.deps.endpoint || run.expires <= this.now())
      throw new IntakeError(410);
    if ((run.retryAt ?? 0) > this.now()) throw new IntakeError(429);
    // The request object is the pending contact's generation identity. Reset
    // removes it; a later submission has a different identity even for this run.
    const contactCancelled = () =>
      request.action === "contact" && run.contact !== request;
    for (let attempt = 0; ; attempt++) {
      // This also runs after every backoff. It cannot retract an issued POST,
      // but prevents sending the abandoned contact again after reset.
      if (contactCancelled()) throw new Error("contact_cancelled");
      try {
        const response = await this.deps.fetch(this.deps.endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-assessment-capability": run.capability,
          },
          body: JSON.stringify(request),
          signal: AbortSignal.timeout(10000),
        });
        if (!response.ok) {
          const raw = response.headers.get("Retry-After");
          const seconds = raw ? Number(raw) : 0;
          const retryAfter = raw
            ? Number.isFinite(seconds)
              ? seconds * 1000
              : Math.max(0, Date.parse(raw) - this.now())
            : 0;
          throw new IntakeError(
            response.status,
            Number.isFinite(retryAfter) ? Math.max(0, retryAfter) : 0,
          );
        }
        const receipt: unknown = await response.json();
        if (!isReceipt(receipt, request)) throw new IntakeError(503);
        return receipt;
      } catch (error) {
        if (contactCancelled()) throw error;
        const retryAfter = error instanceof IntakeError ? error.retryAfter : 0;
        if (retryAfter) run.retryAt = this.now() + retryAfter;
        if (
          attempt === 3 ||
          (error instanceof IntakeError &&
            error.status < 500 &&
            error.status !== 429) ||
          retryAfter > 15000
        )
          throw error;
        await this.wait(
          Math.max(
            retryAfter,
            [1000, 3000, 10000][attempt] * (0.8 + Math.random() * 0.4),
          ),
        );
      }
    }
  }
  private flush(run: Run): Promise<void> {
    if (run.busy) return run.busy;
    run.busy = (async () => {
      let attemptedContact: Run["contact"];
      let startFailed = false;
      try {
        if (!run.started) {
          const startReceipt = await this.send(run, run.start);
          run.attributionStatus = startReceipt.attribution_status;
          run.started = true;
        }
        for (;;) {
          const contact = run.contact;
          if (contact && !run.receipt && contact !== attemptedContact) {
            attemptedContact = contact;
            try {
              const receipt = await this.send(run, contact);
              if (run.contact === contact) {
                run.receipt = receipt;
                delete run.contact;
                this.setState(run.start.run_id, "saved");
                this.emit("assessment_contact_saved", contact, run, "recorded");
                try {
                  this.deps.identify(contact.contact);
                } catch {
                  /* Receipt remains valid. */
                }
              }
            } catch {
              if (run.contact === contact)
                this.setState(run.start.run_id, "not_saved");
            }
          }
          if (!run.pending.length) break;
          try {
            await this.send(run, run.pending[0]);
            run.pending.shift();
            this.persist();
          } catch {
            break;
          }
        }
      } catch {
        startFailed = true;
        if (run.contact) this.setState(run.start.run_id, "not_saved");
      } finally {
        run.busy = undefined;
        this.persist();
        // A contact entered while an event was in flight is independent of that event's failure.
        if (
          !startFailed &&
          run.contact &&
          run.contact !== attemptedContact &&
          !run.receipt
        )
          queueMicrotask(() => {
            void this.flush(run);
          });
      }
    })();
    return run.busy;
  }
}
export const funnelService = new FunnelService({
  enabled: CAMPAIGN_ENABLED,
  endpoint:
    import.meta.env.VITE_MARKETING_INTAKE_URL ||
    (import.meta.env.VITE_SUPABASE_URL
      ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/marketing-intake`
      : ""),
  storage: browserSession(),
  fetch: (...args) => fetch(...args),
  track: (name, props) => telemetry.track(name, props),
  identify: (contact) =>
    identifyUser(contact.email, {
      name: contact.name,
      company: contact.company,
      industry: contact.industry,
      source: "assessment",
    }),
});
